import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTag, FaTimes, FaPlus } from "react-icons/fa";
import { useScrollLock } from "../../hooks/useScrollLock";
import {
  createPromoCode,
  getAllCourses,
} from "../../store/slices/admin/asyncThunks";
import {
  selectPromoCodeCreateLoading,
  selectCourses,
} from "../../store/slices/admin/slice";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormGroup,
  FormLabel,
  FormInput,
  CourseCheckboxList,
  CourseCheckboxItem,
  ModalActions,
  Button,
  ErrorMessage,
} from "./CreatePromoCodeModal.styles";

const CreatePromoCodeModal = ({ isOpen, onClose, onCreated }) => {
  useScrollLock(isOpen);
  const dispatch = useDispatch();
  const createLoading = useSelector(selectPromoCodeCreateLoading);
  const courses = useSelector(selectCourses);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    percentage: "",
    courseIds: [],
  });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({ code: "", name: "", percentage: "", courseIds: [] });
      setFormError("");
      dispatch(getAllCourses({ limit: 100 }));
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const handleCourseToggle = (courseId) => {
    setFormData((prev) => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter((id) => id !== courseId)
        : [...prev.courseIds, courseId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.code.trim()) {
      setFormError("Kod je obavezan");
      return;
    }
    if (!formData.name.trim()) {
      setFormError("Naziv je obavezan");
      return;
    }
    const pct = parseFloat(formData.percentage);
    if (!pct || pct <= 0 || pct > 100) {
      setFormError("Procenat popusta mora biti izmedju 1 i 100");
      return;
    }
    if (formData.courseIds.length === 0) {
      setFormError("Morate odabrati bar jedan kurs");
      return;
    }

    try {
      const result = await dispatch(
        createPromoCode({
          code: formData.code,
          name: formData.name,
          percentage: formData.percentage,
          courseIds: formData.courseIds,
        })
      ).unwrap();

      if (result.success) {
        onClose();
        if (onCreated) onCreated();
      }
    } catch (error) {
      setFormError(error || "Greska pri kreiranju promo koda");
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FaTag /> Novi Promo Kod
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <FormLabel>Kod</FormLabel>
              <FormInput
                type="text"
                placeholder="npr. POPUST20"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Naziv</FormLabel>
              <FormInput
                type="text"
                placeholder="npr. 20% popust"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Popust (%)</FormLabel>
              <FormInput
                type="number"
                min="1"
                max="100"
                placeholder="npr. 20"
                value={formData.percentage}
                onChange={(e) =>
                  setFormData({ ...formData, percentage: e.target.value })
                }
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Kursevi</FormLabel>
              <CourseCheckboxList>
                {courses && courses.length > 0 ? (
                  courses.map((course) => (
                    <CourseCheckboxItem key={course.id}>
                      <input
                        type="checkbox"
                        checked={formData.courseIds.includes(course.id)}
                        onChange={() => handleCourseToggle(course.id)}
                      />
                      {course.title}
                    </CourseCheckboxItem>
                  ))
                ) : (
                  <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                    Ucitavanje kurseva...
                  </span>
                )}
              </CourseCheckboxList>
            </FormGroup>
          </ModalBody>

          {formError && <ErrorMessage>{formError}</ErrorMessage>}

          <ModalActions>
            <Button type="button" $secondary onClick={onClose}>
              Otkazi
            </Button>
            <Button type="submit" $primary disabled={createLoading}>
              <FaPlus /> Kreiraj
            </Button>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreatePromoCodeModal;
